import { Stacked, StackedGrow } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { metricsAtom } from '@/atoms/metric';
import { teamRadarTemplateConfigsAtom } from '@/atoms/queries/userConfigs';
import { RADAR_VIEW, radarViewAtom } from '@/atoms/radar';
import { RadarThreeSixtyUnavailableInfoBar } from '@/components/feedback/RadarThreeSixtyUnavailableInfoBar';
import { VisContentsContainer } from '@/components/layout/VisContentsContainer';
import { RadarCard } from '@/components/radar/RadarCard';
import { RadarCardHeader } from '@/components/radar/RadarCardHeader';
import { RadarExportModal } from '@/components/radar/RadarExportModal';
import { PercentileDataPillLegend } from '@/components/radar/table/PercentileDataPillLegend';
import { RadarTableDisplayProps } from '@/components/radar/table/RadarTable';
import { TeamRadarTable } from '@/components/radar/table/TeamRadarTable';
import { OverviewHeader } from '@/components/section/OverviewHeader';
import { useIsComparisonActive } from '@/hooks/useIsComparisonActive';
import { TeamComparisonLegend } from './TeamComparisonLegend';
import { TeamRadarChart } from './TeamRadarChart';

export const TeamRadarTableCard = ({ isInteractive = true, showSpokes }: RadarTableDisplayProps) => (
  <Stacked direction="column" justifyContent="space-between">
    <div className="border border-canvas-primary-main">
      <TeamRadarTable isInteractive={isInteractive} showSpokes={showSpokes} />
    </div>
    <PercentileDataPillLegend />
  </Stacked>
);

export const TeamRadar = () => {
  const { t } = useTranslation(['radar', 'entity']);
  const isComparisonActive = useIsComparisonActive();
  const isDistributionView = useAtomValue(radarViewAtom) === RADAR_VIEW.DISTRIBUTION;
  const label = t('dropdownLabel', { entity: t('team_one', { ns: 'entity' }) });
  const mainLegend = isComparisonActive && <TeamComparisonLegend id="main" />;

  return (
    <>
      <div className="flex flex-col gap-2 min-h-full">
        <OverviewHeader radarTemplateConfigsAtom={teamRadarTemplateConfigsAtom} label={label} />
        <VisContentsContainer rowBreakpoint="xl">
          <RadarCard>
            <StackedGrow>
              <Stacked direction="column" gap="regular">
                <RadarCardHeader />
                <RadarThreeSixtyUnavailableInfoBar />
              </Stacked>
            </StackedGrow>
            <ScopeProvider atoms={[metricsAtom]}>
              <div className="mx-auto max-w-[550px]">
                {/* the legend leads the distribution list but follows the radar */}
                {isDistributionView && mainLegend}
                <TeamRadarChart id="main" isInteractive />
                {!isDistributionView && mainLegend}
              </div>
            </ScopeProvider>
          </RadarCard>
          <div className={twJoin('bg-canvas-tertiary-main flex-1 p-4', isComparisonActive && 'min-w-0')}>
            <TeamRadarTableCard showSpokes={!isDistributionView} />
          </div>
        </VisContentsContainer>
      </div>

      <RadarExportModal>
        <ScopeProvider atoms={[metricsAtom]}>
          <div>
            <TeamRadarChart id="export" isInteractive={false} />
            {isComparisonActive && <TeamComparisonLegend id="export" />}
          </div>
        </ScopeProvider>
        <TeamRadarTableCard isInteractive={false} />
      </RadarExportModal>
    </>
  );
};
